import React from 'react';
import { Row, Col, Tag } from 'antd';
import ProjectCard from './ProjectCard';
import { Project } from '../../services/retailerApi';

interface ProjectKanbanProps {
  projects: Project[];
  onCardClick: (projectId: string) => void;
  onDeleteProject: (projectId: string) => void;
  onDrop: (projectId: string, target: string) => void;
  onDragOver: (column: string) => void;
  dragOverColumn: string | null;
  activeProjectId?: string;
  getContactDisplayName: (retailerId: string, email?: string) => string;
  refreshingProjectIds?: Record<string, boolean>;
}

const ProjectKanban: React.FC<ProjectKanbanProps> = ({
  projects,
  onCardClick,
  onDeleteProject,
  onDrop,
  onDragOver,
  dragOverColumn,
  activeProjectId,
  getContactDisplayName,
  refreshingProjectIds = {},
}) => {
  const kanbanData = {
    todo: projects.filter(p => p.status === 'open' && p.pushPullStatus === 'push'),
    in_progress: projects.filter(p => p.status === 'open' && p.pushPullStatus === 'pull'),
    on_hold: projects.filter(p => p.status === 'on_hold'),
    closed: projects.filter(p => p.status === 'completed' || p.status === 'cancelled')
  };

  const handleDragOver = (e: React.DragEvent, column: string) => {
    e.preventDefault();
    onDragOver(column);
  };

  const handleDrop = (e: React.DragEvent, target: string) => {
    e.preventDefault();
    const projectId = e.dataTransfer.getData('projectId');
    if (projectId) {
      onDrop(projectId, target);
    }
  };

  const handleDragStart = (e: React.DragEvent, projectId: string) => {
    e.dataTransfer.setData('projectId', projectId);
  };

  return (
    <Row gutter={16} className="kanban-columns" style={{ margin: 0, flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 0 }}>
      {[
        { key: 'todo', title: 'À TRAITER', color: '#ef4444', data: kanbanData.todo },
        { key: 'in_progress', title: 'EN COURS', color: '#3b82f6', data: kanbanData.in_progress },
        { key: 'on_hold', title: 'EN ATTENTE', color: '#f59e0b', data: kanbanData.on_hold },
        { key: 'closed', title: 'CLOS', color: '#94a3b8', data: kanbanData.closed }
      ].map(col => (
        <Col key={col.key} style={{ minWidth: 320, width: '25%' }}>
          <div
            className={`kanban-column ${col.key} ${dragOverColumn === col.key ? 'drag-over' : ''}`}
            onDragOver={(e) => handleDragOver(e, col.key)}
            onDrop={(e) => handleDrop(e, col.key)}
            style={{
              background: '#f1f5f9',
              borderRadius: 14,
              height: 'calc(100vh - 280px)',
              display: 'flex',
              flexDirection: 'column',
              padding: 16,
              border: dragOverColumn === col.key ? '2px dashed #3b82f6' : '1px solid #e2e8f0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', fontWeight: 700, fontSize: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: col.color }} />
                {col.title}
              </div>
              <Tag style={{ borderRadius: 10, border: 'none', background: '#e2e8f0', color: '#64748b', fontWeight: 700 }}>{col.data.length}</Tag>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, paddingRight: 4 }}>
              {col.data.map(p => (
                <ProjectCard 
                  key={p._id} 
                  project={p} 
                  isActive={activeProjectId === p._id}
                  onClick={onCardClick}
                  onDelete={onDeleteProject}
                  onDragStart={handleDragStart}
                  interlocutorName={getContactDisplayName(p.retailerId as string, p.lastEmailFrom)}
                  isRefreshing={Boolean(p._id && refreshingProjectIds[p._id])}
                />
              ))}
            </div>
          </div>
        </Col>
      ))}
    </Row>
  );
};

export default ProjectKanban;
